/**
 * Noise Field — animated grid of dots / lines with accent highlights.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const noiseField: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const text = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const variant = (inputs.variant ?? "dots").toLowerCase();

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const fieldId = `${clipPrefix}-field`;
  const titleId = `${clipPrefix}-title`;
  const subId = `${clipPrefix}-sub`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const cols = 18;
  const rows = 24;
  const cells = [] as string[];
  const cellIds = [] as string[];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const id = `${clipPrefix}-cell-${r}-${c}`;
      cellIds.push(id);
      const isHighlight = (r * 5 + c * 3) % 17 === 0;
      const color = isHighlight ? accent : withAlpha(themeColors.fg, 0.18);
      if (variant === "lines") {
        cells.push(`<div id="${id}" style="position:absolute;left:${(c / cols) * 100}%;top:${(r / rows) * 100}%;width:${100 / cols}%;height:1.5px;background:${color};transform-origin:center;${isHighlight ? `box-shadow:0 0 12px ${withAlpha(accent, 0.6)};` : ""}"></div>`);
      } else {
        cells.push(`<div id="${id}" style="position:absolute;left:${(c / cols) * 100}%;top:${(r / rows) * 100}%;width:6px;height:6px;border-radius:9999px;background:${color};transform:translate(-50%,-50%);${isHighlight ? `box-shadow:0 0 14px ${withAlpha(accent, 0.7)};` : ""}"></div>`);
      }
    }
  }

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
    <div id="${fieldId}" style="position:absolute;inset:0;">${cells.join("")}</div>
    <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:80px;text-align:center;">
      ${text ? `<div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.85}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.0;text-wrap:balance;max-width:1080px;background:${withAlpha(themeColors.bg, 0.55)};padding:18px 28px;border-radius:28px;backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border:1.5px solid ${withAlpha(themeColors.fg, 0.12)};">${escapeHtml(text)}</div>` : ""}
      ${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.8}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(sub)}</div>` : ""}
    </div>
  </div>`;

  const init = [
    text ? gsapSetLine(`#${titleId}`, { opacity: 0, scale: 0.92 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    ...cellIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (text) tweens.push(t.to(`#${titleId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.4));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.6));
  // Stagger reveal in a wave
  cellIds.forEach((id, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const wave = (row + col) * 0.018;
    tweens.push(t.to(`#${id}`, { opacity: 1, duration: 0.5, ease: "raw:\"power1.out\"" }, 0.0 + wave));
    if (variant === "lines") {
      tweens.push(t.to(`#${id}`, { rotation: 360, duration: 6 + (i % 5), repeat: -1, ease: "raw:\"none\"" }, wave));
    } else {
      tweens.push(t.to(`#${id}`, { scale: 1.6, duration: 1.2 + (i % 4) * 0.3, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, wave));
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));
  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
